<script setup lang="ts">
import {computed} from 'vue';
import {Clock} from '@lucide/vue';
import {cn} from '@/lib/utils';
import BaseFlexSpinner from '@/core/components/base/spinner/BaseFlexSpinner.vue';
import Button from '../../../core/components/ui/button/Button.vue';
import HospitalTimelineService from "@/hospital/services/HospitalTimelineService";
import type {HospitalTimelineEntry as IHospitalTimelineEntry} from "@/hospital/interfaces/hospital-timeline";
import HospitalTimelineEntry from "@/hospital/components/timeline/HospitalTimelineEntry.vue";

interface Props {
    data?: IHospitalTimelineEntry[]
    total?: number
    loading: boolean
    error?: string
    loadMore?: () => void
    loadingMore?: boolean
    title?: string
    showHeader?: boolean
    hasMore?: boolean
}

const {title = 'Historique du patient', showHeader = true, ...props} = defineProps<Props>();

defineEmits<{
    'load-more': []
}>();

const formattedLastActivity = computed(() => {
    if (!props.data || props.data.length === 0) return null;

    return new Intl.DateTimeFormat('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    }).format(new Date(props.data[0].created_at));
});

const eventCountText = computed(() => {
    if (!props.total) {
        return '';
    }

    return `${props.total} événement${props.total > 1 ? 's' : ''}`;
});

const entryConfig = (entry: IHospitalTimelineEntry) => {
    return HospitalTimelineService.entryConfig(entry);
};
</script>

<template>
  <div v-if="loading" class="flex justify-center py-8">
    <BaseFlexSpinner />
  </div>

  <div v-else-if="error" class="text-center py-8">
    <div class="text-red-500 mb-2">
      Erreur lors du chargement de l'historique
    </div>
    <p class="text-gray-500 text-sm">
      Veuillez réessayer plus tard
    </p>
  </div>

  <div v-else-if="!data || data.length === 0" class="text-center py-12">
    <Clock class="mx-auto h-12 w-12 text-gray-400 mb-4" />
    <h3 class="text-lg font-medium text-gray-900 mb-2">
      Aucun événement
    </h3>
    <p class="text-gray-500">
      L'historique de ce patient est vide pour le moment.
    </p>
  </div>

  <div v-else class="bg-gray-50 rounded-lg p-6 flex flex-col">
    <div v-if="showHeader" class="mb-6">
      <h2 class="mb-2">
        {{ title }}
      </h2>
      <p class="text-sm text-gray-600">
        {{ eventCountText }}
        <span v-if="data.length > 0 && formattedLastActivity">
          • Dernière activité : {{ formattedLastActivity }}
        </span>
      </p>
    </div>

    <div class="flex gap-4 self-end mb-2">
      <div class="flex items-center gap-1">
        <div class="w-4 h-4 rounded-full bg-primary" />
        <span>Votre établissement</span>
      </div>
      <div class="flex items-center gap-1">
        <div class="w-4 h-4 rounded-full bg-secondary" />
        <span>Autres établissements</span>
      </div>
    </div>

    <div class="relative">
      <div class="absolute left-1/2 top-5 bottom-0 w-px bg-gray-200 transform -translate-x-1/2 z-0" />

      <div class="space-y-6">
        <div
          v-for="entry in data"
          :key="entry.id"
          class="relative flex items-start"
        >
          <div
            v-if="!HospitalTimelineService.isFromCurrentEnvironment(entry)"
            class="flex items-start w-full"
          >
            <div class="w-1/2 pr-8">
              <HospitalTimelineEntry
                :entry="entry"
                :config="entryConfig(entry)"
              />
            </div>
            <div class="w-1/2" />
          </div>

          <div
            v-else
            class="flex items-start w-full"
          >
            <div class="w-1/2" />
            <div class="w-1/2 pl-8">
              <HospitalTimelineEntry
                :entry="entry"
                :config="entryConfig(entry)"
              />
            </div>
          </div>

          <div
            :class="cn(
              'absolute left-1/2 top-0 flex items-center justify-center w-10 h-10 rounded-full flex-shrink-0 z-10 transform -translate-x-1/2 bg-white border-2',
              HospitalTimelineService.isFromCurrentEnvironment(entry) && 'border-primary',
              !HospitalTimelineService.isFromCurrentEnvironment(entry) && 'border-secondary'
            )"
          >
            <component :is="entryConfig(entry).icon" class="w-5 h-5 text-black" />
          </div>
        </div>

        <div
          v-if="loadMore && total && data?.length < total"
          class="flex justify-center"
        >
          <Button
            variant="outline"
            :disabled="loadingMore || !hasMore"
            class="max-w-md mx-auto z-20"
            @click="$emit('load-more')"
          >
            <BaseFlexSpinner v-if="loadingMore" class="w-4 h-4" />
            <span v-else>Voir plus</span>
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.space-y-6 > * + * {
    margin-top: 1.5rem;
}
</style>
